// Consistent version of `useMutableSource`, Inspired by https://github.com/pmndrs/valtio/blob/master/src/useMutableSource.ts
import {useEffect, useRef, useState} from 'react';

const TARGET = Symbol('target');
const GET_VERSION = Symbol('getVersion');

export type Source<TargetType, VersionType> = {
  [TARGET]: TargetType;
  [GET_VERSION]: (target: TargetType) => VersionType;
};

function createMutableSource<TargetType, VersionType>(
  target: TargetType,
  getVersion: (target: TargetType) => VersionType
): Source<TargetType, VersionType> {
  return {
    [TARGET]: target,
    [GET_VERSION]: getVersion
  };
}

function useMutableSource<ValueType, TargetType, VersionType>(
  source: Source<TargetType, VersionType>,
  getSnapshot: (target: TargetType) => ValueType,
  subscribe: (target: TargetType, callback: () => void) => () => void
): ValueType {
  const lastVersoin = useRef<VersionType>();
  const currentVersion = source[GET_VERSION](source[TARGET]);
  const [state, setState] = useState((): [
    typeof source,
    typeof getSnapshot,
    typeof subscribe,
    typeof currentVersion,
    ValueType
  ] => [source, getSnapshot, subscribe, currentVersion, getSnapshot(source[TARGET])]);
  let currentSnapshot = state[4];

  if (
    state[0] !== source ||
    state[1] !== getSnapshot ||
    state[2] !== subscribe ||
    (currentVersion !== state[3] && currentVersion !== lastVersoin.current)
  ) {
    currentSnapshot = getSnapshot(source[TARGET]);
    setState([source, getSnapshot, subscribe, currentVersion, currentSnapshot]);
  }
  useEffect(
    () => {
      let didUnsubscribe = false;
      const checkForUpdates = () => {
        if (didUnsubscribe) {
          return;
        }
        lastVersoin.current = source[GET_VERSION](source[TARGET]); // nextVersion
        const nextSnapshot = getSnapshot(source[TARGET]);
        setState((prevState) => {
          if (prevState[0] !== source || prevState[1] !== getSnapshot || prevState[2] !== subscribe) {
            return prevState;
          }
          if (prevState[4] === nextSnapshot) {
            return prevState;
          }
          return [
            prevState[0],
            prevState[1],
            prevState[2],
            lastVersoin.current as VersionType,
            nextSnapshot
          ];
        });
      };
      const unsubscribe = subscribe(source[TARGET], checkForUpdates);
      checkForUpdates();
      return () => {
        unsubscribe();
        didUnsubscribe = true;
      };
    },
    [source, getSnapshot, subscribe] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return currentSnapshot;
}

const globalStore = {
  state: {},
  version: 0,
  listeners: new Set<() => any>(),

};
const globalStoreSource = createMutableSource(
  globalStore,
  (store) => store.version
);

const cache = new Map();

const getSnapshot = (store: typeof globalStore) => {
  const setState = (
    cb: (prevState: typeof store.state) => typeof store.state
  ) => {
    store.state = cb({ ...store.state });
    store.version++;
    store.listeners.forEach((listener) => listener());
  };
  if (!cache.has(store.state) || !cache.has(store)) {
    cache.clear(); // remove all the old references
    cache.set(store.state, [{ ...store.state }, setState]);
    // we cache the result to prevent the useless re-renders
    // the key (store.state) is more consistent than the { ...store.state },
    // because this changes everytime as a new object, and it always going to create a new cache
    cache.set(store, store); // check the above if statement, if the store changed completely (reference change), we'll make a new result and new state
  }

  return cache.get(store.state); // [state, setState]
};

const subscribe = (store: typeof globalStore, callback: () => any) => {
  store.listeners.add(callback);
  return () => store.listeners.delete(callback);
};

const useContext = (): any => {
  return useMutableSource(globalStoreSource, getSnapshot, subscribe);
} // returns [state, setState]

export {createMutableSource, useContext, useMutableSource};
